// js/components/Accounts/Account/Communications/Communications.js
import React, {useEffect, useState, useMemo, useCallback} from 'react';
import {useTranslation} from "react-i18next";

import axios from "../../../../axios-defaults";
import Aux from '../../../../hoc/Auxiliary';
import formatDate from "../../../Utils/Date/formatDate";
import TableContainer from './Table/TableContainer';
import DateFormated from "../../../Utils/Date/Date";
import DateFilters, {DEFAULT_DATE, TODAY} from "../Tables/DateFilters";
import Spinner from "../../../UI/Spinner";
import SearchForm from "../../../UI/SearchForm/SearchForm";
import Sorter from "../../../UI/Toolbar/Sorter";
import SelectWrapper from "../../../Utils/Input/SelectWrapper";
import {fireGTMDownloadFileEvent} from "../../../Utils/GTM/GTMHelper";
import TextTruncate from "../../../UI/TextTruncate";

const communications = (props) => {
    const { t, i18n } = useTranslation();

    const { title,
            typeLabel,
            component,
            startDate,
            endDate,
            pageNumber,
            pageSize,
            subAccountCode,
            communicationTypesExcluded,
            communicationType} = props;

    const [documentsStatus, setDocumentsStatus] = useState(null);
    const [originalDatas, setOriginalDatas] = useState([]);
    const [communications, setCommunications] = useState([]);
    const [communicationTypes, setCommunicationTypes] = useState([]);
    const [filters, setFilters] = useState({unreadOnly: false, communicationType: ''});
    //const [filteredData, setFilteredData] = ([]);
    const [pageIndex, setPageIndex] = useState(0);
    const [loading, setLoading] = useState(0);
    const [sortField, setSortField] = useState('communicationDate');
    const [sortDirection, setSortDirection] = useState('DESC');
    const [sortTableField, setSortTableField] = useState('communicationDate');
    const [sortTableDirection, setSortTableDirection] = useState('DESC');

    const descAccessor = ((i18n.language === 'fr') ? 'descriptionFr' : 'descriptionEn');

    const getCommunications = async () => {

        let params = '';
        if (pageNumber && pageSize) {
            params = `subAccountCode=${subAccountCode}&pageNumber=${pageNumber}&pageSize=${pageSize}`
        } else {
            if (startDate === null || endDate === null) {
                return;
            } else {
                const currentStartDate = (startDate)?startDate:DEFAULT_DATE;
                const currentEndDate = (endDate)?endDate:formatDate(TODAY);
                params = `subAccountCode=${subAccountCode}&startDate=${currentStartDate}&endDate=${currentEndDate}`;
            }
        }

        if (communicationType) {
            params = `${params}&communicationTypeCode=${communicationType}`
        }

        await axios.get(`/subAccounts/communications?${params}`)
            .then(response => {
                if (response.data.succeeded) {
                    const datas = response.data.data;
                    let filteredData = datas;
                    if (Array.isArray(communicationTypesExcluded)) {
                        filteredData = datas.filter(item => {
                            return communicationTypesExcluded.indexOf(item.communicationType.code) == -1;
                        });
                    }
                    filteredData.map((item) => {
                        const docStatus = documentsStatus.find((data) => {
                            return data['document_path'] == item.documentPath;
                        });
                        item.status = !docStatus?'U':(docStatus.document_status);
                    })
                    setCommunications(filteredData)
                    setOriginalDatas(filteredData)
                }
            }).catch(error => {
                console.log('[Account.js] : Communications ajax error in getCommunications()',error.message)
            });
    }

    useEffect(async () => {
        await getCommunicationTypes();
        await getDocumentStatus();
    }, [])

    useEffect(async () => {
        if (documentsStatus !== null) {
            setLoading(true);
            await getCommunications();
            setLoading(false);
        }
    }, [startDate, endDate, pageNumber, pageSize, documentsStatus])

    const getDocumentStatus = async () => {
        await axios.get(`/documents_status`)
            .then(response => {
                if (response.data.succeeded) {
                    setDocumentsStatus(response.data.data);
                }
            }).catch(error => {
                console.log('[Account.js] : DocumentStatus ajax error in getDocumentStatus()',error.message);
            });
    }

    const getCommunicationTypes = async () => {
        await axios.get(`/communications/communicationTypes`)
            .then(response => {
                if(response.data.succeeded){
                    setCommunicationTypes(response.data.data);
                }
            }).catch(error => {
                console.log('[Communications.js] : Ajax error in getCommunicationTypes()', error.message)
            }
        );
    }

    const filteredItems = useMemo(() => {
        let newItems = [...communications];
        let filteredData = newItems.filter(item => {
            let isMatching = true;
            Object.entries(filters).forEach(entry  => {
                const [key, value] = entry;
                if (value) {
                    switch(key){
                        case 'communicationType':
                            isMatching = item.communicationType.code === value && isMatching;
                            break;
                        case 'unreadOnly':
                            isMatching = item.status !== 'R' && isMatching;
                            break;
                        default: isMatching = true;
                    }
                }
            })

            return isMatching;
        });

        return filteredData;
    }, [communications, filters]);

    const typeFilterHandler = (filter, value) => {
        const newFilters = { ...filters, [filter] : value }
        setFilters(newFilters);
    }

    const onSearchDataUpdate = (searchData) => {
        if (Array.isArray(searchData)) {
            return setCommunications(searchData);
        } else {
            return null;
        }
    }

    const storeSortedItems = (sortedItems) => {
        if (sortedItems.length > 0) {
            setCommunications(sortedItems);
        }
    }
    const handlePage = (pageIndex) => {
        setPageIndex(pageIndex);
    }

    const handleSort = useCallback((sortBy = null) => {
		if (sortBy && sortBy.length > 0) {
			setSortField(sortBy[0].id);
			setSortDirection(sortBy[0].desc ? 'DESC' : 'ASC');
		} else {
			setSortField(sortField);
            setSortDirection(sortDirection);
			setSortTableField(sortField);
			setSortTableDirection(sortDirection);
		}
    }, [sortField,sortDirection])

	const setSorterCallback = useCallback((columnId, direction) => {
		setSortTableField(columnId);
		setSortTableDirection(direction);
		setSortField(columnId);
		setSortDirection(direction);
	}, []);

    const setSortByCallback = useMemo(() =>
            [{desc:sortTableDirection==='DESC', id:sortTableField}]
        , [sortTableDirection, sortTableField]);

	const onClickAction = (filename) => {
		axios.get(`/documents/download?filename=${encodeURI(filename)}`)
			.then(response => {
				if (response.data.succeeded) {
					const updatedCommunications = communications.map(item => {
						if (item.documentPath === filename) {
							return { ...item, status: 'R' }; // Shallow copy of object
						} else {
							return item;
						}
					});

					// Update with modified objects
					setCommunications(updatedCommunications);

					// Open a new tab with the download result
					window.open(`/rest/v1/communications/download?filename=${encodeURI(filename)}`);
				}
			})
			.catch(error => {
				console.log('[Communication.js] : DocumentStatus ajax error in onClickAction()', error.message);
			});
	};

	useEffect(() => {
		// Reapply sorting fields after updating communications
		handleSort();
	}, [communications]);

	const columns = useMemo(
        () => [
            {
                Header: t('communications.table.headers.subject'),
                accessor: 'documentationDescription',
                Cell: (props) => {
                    return (props.value) ? (
						<strong>
							<TextTruncate length={32} multiline={2}>
								{props.value}
							</TextTruncate>
						</strong>
					) : '';
                },
            },
            {
				Header: t('communications.table.headers.document'),
                accessor: 'communicationType',
                Cell: (props) => {
                    return (props.value[descAccessor]) ? props.value[descAccessor] : '';
                }
            },
			{
				Header: t('communications.table.headers.senton'),
				accessor: 'communicationDate',
				sort: 'desc',
				Cell: (props) => {
					return (props.value) ? <DateFormated date={props.value} withTime={true}/> : '';
				},
				sortMethod: (a, b) => {
					var a1 = new Date(a).getTime();
					var b1 = new Date(b).getTime();
					if (a1 < b1)
						return 1;
					else if (a1 > b1)
						return -1;
					else
						return 0;
				}
			},
            {
                Header: t('docs.invoices.table.headers.status'),
                accessor: 'status',
                Cell: ({cell}) => {
                    const {value} = cell;
                    const pickStatus = (value) => {
                        let status;
                        if (value === 'R') {
                            status = <div className="status status--black" style={{minWidth:"78px"}}><i className="fas fa-eye"></i>&nbsp;{t('communications.table.read')}</div>;
                        } else {
                            status = <div className="status status--red" style={{minWidth:"78px"}}><i className="fas fa-eye-slash"></i>&nbsp;{t('communications.table.unread')}</div>;
                        }
                        return status;
                    };
                    return (
                        <div style={{textAlign: 'left', fontSize: 16}}>
                            {pickStatus(value)}
                        </div>
                    );
                }
            },
            {
                Header: () => null,
                id: 'file', // 'id' is required
                accessor: 'documentPath',
                Cell: (props) => {
                    if(props.value){
                        return (
                            <a onClick={(e) => {
                                    e.preventDefault();
                                    onClickAction(props.value);
								if(props.row.original.communicationType.code === "F"){
									fireGTMDownloadFileEvent('facture', 'PDF')
								}else if(props.row.original.communicationType.code === "R"){
									fireGTMDownloadFileEvent('releve', 'PDF')
								}else if(props.row.original.communicationType.code === "L"){
									fireGTMDownloadFileEvent('priceletter', 'PDF')
								}else if(props.row.original.communicationType.code === "E"){
									fireGTMDownloadFileEvent('cashing', 'PDF')
								}else if(props.row.original.communicationType.code === "P"){
									fireGTMDownloadFileEvent('delproof', 'PDF')
								}
							}}
                                className="link link--red link--upper link--accent link--fs-14 d-inline-block">
                                <strong>{t('communications.table.downloadpdf')}&nbsp;&nbsp;<i className="fas fa-angle-right"></i></strong>
                            </a>
                        );
                    } else { return ''; }
                }

            },
        ],
        [ t, i18n, filteredItems]
    );

    return (
            <Aux>
                <h2 className="headline headline--section headline--uppercase">{title}</h2>
                <Aux>
                    <SearchForm data={originalDatas} currentData={filteredItems} updateData={onSearchDataUpdate} />
                    <br/>
                    <DateFilters
                        startDate={props.startDate}
                        endDate={props.endDate}
                        setStartDate={props.setStartDate}
                        setEndDate={props.setEndDate}
                        changeDateFilterType={props.changeDateFilterType}
                        dateFilterType={props.dateFilterType}
                        enableCurrentPeriod={props.enableCurrentPeriod}
                        component={component}
                        labelFilterAll={t("docs.invoices.table.filter.all.documents")}
                    />

                    {!loading && communications.length !== 0 ? <React.Fragment>

                        <h3 className="headline headline--block mt-4">{filteredItems.length} {typeLabel}</h3>
                        <div className="datatable__filters flex flex-column  flex-md-row flex--wrap flex--justify-start align-start align-sm-center">
                            <div className="w-md-100 datatable__filters__group flex flex-column flex-sm-row flex--wrap flex--justify-start align-items-sm-center align-items-start">
                                <strong>{t('misc.filter')}&nbsp;&nbsp;</strong>
                                <div className="mb-2 mb-sm-0 mr-0 mr-sm-4 selectcontainer">
                                    <SelectWrapper>
                                        <select id="documenttype-filters"
                                            onChange={(e) => {
                                                let selectValue = e.target.value;
                                                typeFilterHandler('communicationType', selectValue);
                                            }}
                                            value={filters.communicationType}
                                        >
                                            <option value="">{t('communications.table.headers.communication_type')}</option>
                                            {communicationTypes.map(type => {
                                                return (
                                                    <option value={type.code} key={type.code}>
                                                        {(i18n.language === "fr") ? type.descriptionFr : type.descriptionEn}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </SelectWrapper>
                                </div>
                            </div>
                            <label className="w-md-100 mb-2 mb-sm-0 checkbox-field checkbox-field--gray mt-3 mt-md-0 flex align-self-center" htmlFor="unread_only">
                                <input type="checkbox" name="unread_only" id="unread_only"
                                       checked={filters.unreadOnly}
                                       onChange={(e) => typeFilterHandler('unreadOnly', e.target.checked)} />
                                <span></span>
                                <strong>&nbsp;&nbsp;{t('communications.filters_groups.unread.label')}</strong>
                            </label>
                            <div className="w-md-100 filters__right flex--justify-self-end ml-0 ml-md-auto">
                                <Sorter
                                    sortOptions={[
										{
                                            label: t('communications.table.headers.subject'),
                                            id: 'documentationDescription',
                                            value: 'documentationDescription',
                                            type: 'string'
                                        },{
                                            label: 'Document',
                                            id: 'communicationType',
                                            value: 'communicationType.'+descAccessor,
                                            type: 'string'
                                        },{
                                            label: t('communications.table.headers.senton'),
                                            id: 'communicationDate',
                                            value: 'communicationDate',
                                            type: 'date'
                                        },{
                                            label: t('docs.invoices.table.headers.status'),
                                            id: 'status',
                                            value: 'documentStatus.code',
                                            type: 'String'
                                        }
                                    ]}
                                    items={filteredItems}
                                    getSortedItems={storeSortedItems}
                                    setSortByCallback={setSorterCallback}
                                    sortDirection={sortDirection}
                                    sortField={sortField}
                                />
                            </div>
                        </div>

                        <TableContainer
                            columns={columns}
                            data={filteredItems}
                            typeLabel={typeLabel}
                            onPage={handlePage}
                            onSort={handleSort}
                            setSortByCallback={setSortByCallback}
                            initialPageIndex={pageIndex}/>
                    </React.Fragment> :  (loading)
                        ? <Spinner/>
                        : (
                            <h3 className="headline headline--block mt-4">{filteredItems.length} {typeLabel}</h3>
                        )
                    }
                </Aux>

            </Aux>
    );
};
export default communications;
